import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Skeleton,
  Box,
  Divider,
  Stack,
  TableContainer,
  Tooltip,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import BoltIcon from "@mui/icons-material/Bolt";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import {
  ASSETICONBASEURL,
  P2PWEBSERVICE,
  showToast,
  WEBSERVICE,
} from "../../CoreDataService";
import { useSelector } from "react-redux";
import { ContainedButton, OutlinedButton } from "../../Common/Buttons";

const P2PWalletTable = () => {
  const { btncolor } = useSelector((state) => state);
  const [walletDetails, setWalletDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState("");
  const [fundsFromValue, setFundsFromValue] = useState("SPOT WALLET");
  const [fundsToValue, setFundsToValue] = useState("P2P WALLET");
  const [fundAmount, setFundAmount] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAssetBalance, setSelectedAssetBalance] = useState("");

  const fetchWalletDetails = async () => {
    const uuid = localStorage.getItem("uuid");
    setLoading(true);
    try {
      const response = await axios.post(
        `${WEBSERVICE}crypto/banking/p2p/getP2PWalletBalance`,
        JSON.stringify({ uuid }),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.error.error_data === 0) {
        setWalletDetails(response.data.p2pWalletDetails || []);
      } else {
        showToast("error", response.data.error.error_msg);
      }
    } catch (error) {
      console.error(error);
      showToast("error", "Failed to fetch wallet data.");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchWalletDetails();
  }, []);

  const openFundTransferModal = (index) => {
    const selected = walletDetails[index];
    setSelectedAsset(selected.assets);
    setSelectedAssetBalance(selected.spotWalletBalance);
    setFundsFromValue("SPOT WALLET");
    setFundsToValue("P2P WALLET");
    setFundAmount("");
    setIsModalOpen(true);
  };

  const swapValues = () => {
    setFundsFromValue((prev) =>
      prev === "SPOT WALLET" ? "P2P WALLET" : "SPOT WALLET"
    );
    setFundsToValue((prev) =>
      prev === "P2P WALLET" ? "SPOT WALLET" : "P2P WALLET"
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(fundAmount) <= 0) {
      showToast("error", "Amount must be greater than 0");
      return;
    }
    setSubmitting(true);
    const uuid = localStorage.getItem("uuid");
    const action =
      fundsFromValue === "SPOT WALLET" ? "P2P WALLET" : "SPOT WALLET";

    const payload = {
      uuid,
      assets: selectedAsset,
      amount: fundAmount,
      action,
      flag: 0,
    };

    try {
      const response = await axios.post(
        `${P2PWEBSERVICE}/addBalanceSpotToP2PAndP2PToSpot`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.error_data === 0) {
        showToast("success", "Fund has been transferred successfully");
        setTimeout(() => {
          setIsModalOpen(false);
          setFundsFromValue("SPOT WALLET");
          setFundsToValue("P2P WALLET");
          setFundAmount("");
          setSelectedAsset("");
          fetchWalletDetails();
        }, 1000);
      } else {
        showToast("error", response.data.error_msg);
      }
    } catch (error) {
      console.error(error);
      showToast("error", "Failed to submit transfer.");
    } finally {
      setSubmitting(false);
    }
  };

  const maxFundTransfer = async (index) => {
    const payload = {
      uuid: localStorage.getItem("uuid"),
      assets: walletDetails[index]?.assets,
      amount: walletDetails[index]?.spotWalletBalance,
      action: "P2P WALLET",
      flag: 1,
    };

    try {
      const response = await axios.post(
        `${P2PWEBSERVICE}/addBalanceSpotToP2PAndP2PToSpot`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.error_data === 0) {
        showToast("success", "Fund has been transferred successfully");
        fetchWalletDetails();
      } else {
        showToast("error", response.data.error_msg);
      }
    } catch (error) {
      console.error(error);
      showToast("error", "Failed to perform max fund transfer.");
    }
  };

  return (
    <Box sx={pageWrapper}>
      {/* ── Hero Banner ── */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.95
          )} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <AccountBalanceWalletOutlinedIcon
              sx={{ color: "#fff", fontSize: 30 }}
            />
          </Box>
          <Box>
            <Typography sx={heroTitle}>P2P Wallet</Typography>
            <Typography sx={heroSubtitle}>
              Manage your P2P wallet balances and transfer funds between your
              Spot and P2P wallets instantly
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Main Card ── */}
      <Box sx={mainCard}>
        {/* Card Header */}
        <Box sx={cardHeader}>
          <Box>
            <Typography sx={cardHeaderTitle}>Wallet Balances</Typography>
            <Typography sx={cardHeaderCaption}>
              {walletDetails.length} asset{walletDetails.length !== 1 ? "s" : ""} in your P2P wallet
            </Typography>
          </Box>
          <Tooltip title="Refresh balances" arrow>
            <IconButton
              onClick={fetchWalletDetails}
              size="small"
              sx={{
                color: "var(--text-color)",
                opacity: 0.7,
                "&:hover": { opacity: 1 },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ borderColor: "rgba(125,125,125,0.13)" }} />

        {/* ── Table ── */}
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <TableContainer sx={modernTableContainer}>
            <Table sx={tableStyle}>
              <TableHead>
                <TableRow sx={modernTableHeadRow}>
                  {["Asset", "Available Balance", "Actions"].map((h, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        ...modernTableHeadCell,
                        textAlign: i === 2 ? "right" : "left",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton
                            variant="rounded"
                            height={20}
                            sx={{ borderRadius: "6px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Skeleton
                            variant="rounded"
                            height={20}
                            sx={{ borderRadius: "6px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Skeleton
                            variant="rounded"
                            height={20}
                            sx={{ borderRadius: "6px" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  : walletDetails.length > 0
                  ? walletDetails
                      .sort((a, b) => b?.amount - a.amount)
                      .map((wallet, index) => (
                        <TableRow key={index} sx={modernTableBodyRow}>
                          <TableCell sx={modernTableBodyCell}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                              }}
                            >
                              <Box sx={assetIconWrap}>
                                <img
                                  alt={wallet.assets}
                                  src={`${ASSETICONBASEURL}${wallet.assets.toUpperCase()}.png`}
                                  width={24}
                                  height={24}
                                  style={{ borderRadius: "50%", display: "block" }}
                                />
                              </Box>
                              <Typography sx={assetName}>
                                {wallet.assets}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={modernTableBodyCell}>
                            <Typography sx={balanceText}>
                              {wallet.amount}
                            </Typography>
                            <Typography sx={balanceSubText}>
                              {wallet.assets}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{ ...modernTableBodyCell, textAlign: "right" }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                            >
                              <Tooltip title="Transfer max spot balance to P2P" arrow>
                                <span>
                                  <OutlinedButton
                                    onClick={() => maxFundTransfer(index)}
                                    size="small"
                                    variant="outlined"
                                    startIcon={<BoltIcon sx={{ fontSize: 14 }} />}
                                    sx={maxBtn}
                                  >
                                    Max
                                  </OutlinedButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Transfer funds between wallets" arrow>
                                <span>
                                  <ContainedButton
                                    onClick={() => openFundTransferModal(index)}
                                    size="small"
                                    variant="contained"
                                    startIcon={
                                      <SwapHorizOutlinedIcon sx={{ fontSize: 14 }} />
                                    }
                                    sx={addFundsBtn}
                                  >
                                    Transfer
                                  </ContainedButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                  : (
                    <TableRow>
                      <TableCell colSpan={3} sx={emptyCell}>
                        <InboxOutlinedIcon sx={emptyIcon} />
                        <Typography sx={emptyText}>
                          No wallet assets found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* ── Transfer Modal ── */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>
          Fund Transfer
          <IconButton
            onClick={() => setIsModalOpen(false)}
            size="small"
            sx={dialogCloseBtn}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />

        <DialogContent sx={dialogContentStyle}>
          <Typography sx={dialogInfoText}>
            Internal transfers between wallets are free of charge.
          </Typography>

          <Box sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* From field */}
            <Box>
              <Typography sx={dialogFieldLabel}>From</Typography>
              <Box sx={walletDisplayBox}>
                <Typography sx={walletDisplayText}>{fundsFromValue}</Typography>
              </Box>
            </Box>

            {/* Swap button */}
            <Box sx={{ display: "flex", justifyContent: "center", my: 0.5 }}>
              <Tooltip title="Swap direction" arrow>
                <IconButton
                  onClick={swapValues}
                  size="small"
                  sx={{
                    backgroundColor: alpha(btncolor || "#1976d2", 0.1),
                    border: `1px solid ${alpha(btncolor || "#1976d2", 0.25)}`,
                    color: btncolor,
                    "&:hover": {
                      backgroundColor: alpha(btncolor || "#1976d2", 0.2),
                    },
                  }}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* To field */}
            <Box>
              <Typography sx={dialogFieldLabel}>To</Typography>
              <Box sx={walletDisplayBox}>
                <Typography sx={walletDisplayText}>{fundsToValue}</Typography>
              </Box>
            </Box>

            {/* Asset field */}
            <Box>
              <Typography sx={dialogFieldLabel}>Asset</Typography>
              <Box
                sx={{
                  ...walletDisplayBox,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <img
                  alt={selectedAsset}
                  src={`${ASSETICONBASEURL}${selectedAsset.toUpperCase()}.png`}
                  width={20}
                  height={20}
                  style={{ borderRadius: "50%" }}
                />
                <Typography sx={walletDisplayText}>{selectedAsset}</Typography>
              </Box>
            </Box>

            {/* Amount field */}
            <Box>
              <Typography sx={dialogFieldLabel}>Amount</Typography>
              <TextField
                type="number"
                value={fundAmount}
                onChange={(e) => setFundAmount(e.target.value)}
                fullWidth
                required
                placeholder="0.00"
                helperText={`Available spot wallet balance: ${selectedAssetBalance} ${selectedAsset}`}
                slotProps={{
                  input: {
                    inputProps: { min: 0, step: "any" },
                    endAdornment: (
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "var(--text-color)",
                          opacity: 0.5,
                          pr: 1,
                          userSelect: "none",
                        }}
                      >
                        {selectedAsset}
                      </Typography>
                    ),
                  },
                  formHelperText: { sx: { color: "var(--text-color)", opacity: 0.6 } },
                }}
                sx={dialogTextField}
              />
            </Box>
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogActions sx={dialogActionsStyle}>
          <OutlinedButton
            onClick={() => setIsModalOpen(false)}
            variant="outlined"
            sx={{ minWidth: 110, height: 42, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </OutlinedButton>
          <ContainedButton
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{ minWidth: 160, height: 42, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
          >
            {submitting ? "Transferring…" : "Confirm Transfer"}
          </ContainedButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 600,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const cardHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: { xs: 2, md: 3 },
  py: 2,
  backgroundColor: "var(--body-color-less2)",
};

const cardHeaderTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const cardHeaderCaption = {
  fontSize: "0.75rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.25,
};

const modernTableContainer = {
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "none",
  overflow: "hidden",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 500,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
};

const assetIconWrap = {
  width: 36,
  height: 36,
  borderRadius: "50%",
  border: "1.5px solid rgba(125,125,125,0.18)",
  backgroundColor: "var(--body-color-less2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  overflow: "hidden",
};

const assetName = {
  fontSize: "0.9rem",
  fontWeight: 700,
  color: "var(--text-color)",
  letterSpacing: "0.01em",
};

const balanceText = {
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "var(--text-color)",
  lineHeight: 1.3,
};

const balanceSubText = {
  fontSize: "0.68rem",
  color: "var(--text-color)",
  opacity: 0.45,
  lineHeight: 1.2,
  mt: 0.25,
};

const maxBtn = {
  height: 32,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.78rem",
};

const addFundsBtn = {
  height: 32,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.78rem",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

// Dialog styles
const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "85%", sm: 460 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.1rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
  position: "relative",
};

const dialogCloseBtn = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--text-color)",
  opacity: 0.6,
};

const dialogContentStyle = {
  pt: 2.5,
  pb: 2,
  px: { xs: 2.5, md: 3 },
  backgroundColor: "var(--popupbody-color)",
};

const dialogInfoText = {
  fontSize: "0.82rem",
  color: "var(--text-color)",
  opacity: 0.65,
  lineHeight: 1.6,
};

const dialogFieldLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.55,
  mb: 0.75,
};

const walletDisplayBox = {
  backgroundColor: "var(--body-color-less2)",
  border: "1px solid rgba(125,125,125,0.2)",
  borderRadius: "10px",
  px: 2,
  py: 1.25,
};

const walletDisplayText = {
  fontSize: "0.88rem",
  fontWeight: 600,
  color: "var(--text-color)",
};

const dialogTextField = {
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.45)",
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
};

const dialogActionsStyle = {
  px: 3,
  py: 2.5,
  backgroundColor: "var(--popupbody-color)",
  justifyContent: "flex-end",
  gap: 1,
};

export default P2PWalletTable;
